import api from '@/services/api';
import type { TemporaryPresetValue } from '@/auth/auth-types';

const BASE = '/v2/iam';

// ─── Organization ───────────────────────────────────────────────────────────

export const iamApi = {
    /** Update current user's own profile (firstName / lastName). */
    updateMe: (data: { firstName?: string; lastName?: string }) =>
        api.patch<{ _id: string; email: string; firstName?: string; lastName?: string; isStaff?: boolean }>(
            `${BASE}/me`,
            data,
        ),

    createOrg: (data: {
        name: string;
        domain: string;
        description?: string;
        website?: string;
        temporaryPreset?: TemporaryPresetValue;
    }) =>
        api.post(`${BASE}/org`, data),

    getOrg: () => api.get(`${BASE}/org`),

    updateOrg: (data: Record<string, unknown>) => api.patch(`${BASE}/org`, data),

    // ─── Workspace ──────────────────────────────────────────────────────────

    createWorkspace: (data: { name: string; description?: string }) =>
        api.post(`${BASE}/workspace`, data),

    listWorkspaces: () => api.get(`${BASE}/workspace`),

    updateWorkspace: (id: string, data: { name?: string; description?: string }) =>
        api.patch(`${BASE}/workspace/${id}`, data),

    deleteWorkspace: (id: string) => api.delete(`${BASE}/workspace/${id}`),

    // ─── Roles ──────────────────────────────────────────────────────────────

    listRoles: () => api.get(`${BASE}/roles`),

    createRole: (data: { name: string; description?: string; scope?: string; permissions: string[] }) =>
        api.post(`${BASE}/roles`, data),

    updateRole: (id: string, data: { name?: string; description?: string; permissions?: string[] }) =>
        api.patch(`${BASE}/roles/${id}`, data),

    deleteRole: (id: string) => api.delete(`${BASE}/roles/${id}`),

    getPermissions: () => api.get(`${BASE}/permissions`),

    // ─── Members ────────────────────────────────────────────────────────────

    listMembers: () => api.get(`${BASE}/members`),

    getMemberRoles: (id: string) => api.get(`${BASE}/members/${id}/roles`),

    /** Set the member's role in a single workspace (replaces existing). */
    setMemberWorkspaceRole: (id: string, data: { workspaceId: string; roleId: string }) =>
        api.patch(`${BASE}/members/${id}/role`, data),

    /** Remove the member from a single workspace. */
    removeMemberWorkspaceRole: (id: string, data: { workspaceId: string }) =>
        api.delete(`${BASE}/members/${id}/role`, { data }),

    /** Promote/demote a member to/from organization admin. */
    setMemberOrgAdmin: (
        id: string,
        data: { isOrgAdmin: boolean; fallbackWorkspaceRoles?: { workspaceId: string; roleId: string }[] },
    ) => api.patch(`${BASE}/members/${id}/org-admin`, data),

    removeMember: (id: string) => api.delete(`${BASE}/members/${id}`),

    // Legacy (deprecated)
    addMemberRole: (id: string, data: { roleId: string; workspaceId?: string }) =>
        api.post(`${BASE}/members/${id}/roles`, data),

    removeMemberRole: (id: string, data: { roleId: string; workspaceId?: string }) =>
        api.delete(`${BASE}/members/${id}/roles`, { data }),

    // ─── Invites ────────────────────────────────────────────────────────────

    /** Preview what would happen when sending invites to these emails. */
    previewInvite: (data: { emails: string[]; workspaceId?: string; roleId?: string; asOrgAdmin?: boolean }) =>
        api.post(`${BASE}/invite/preview`, data),

    sendInvite: (data: { email?: string; roleId?: string; workspaceId?: string; inviteId?: string }) =>
        api.post(`${BASE}/invite`, data),

    listInvites: () => api.get(`${BASE}/invites`),

    /** Resend a pending invite (regenerates token + extends expiry). */
    resendInvite: (id: string) => api.post(`${BASE}/invites/${id}/resend`),

    revokeInvite: (id: string) => api.delete(`${BASE}/invites/${id}`),

    validateInvite: (token: string) => api.get(`${BASE}/invite/validate/${token}`),

    acceptInvite: (token: string) => api.post(`${BASE}/invite/accept`, { token }),

    /** Inline-OTP flow used on the public /accept-v2-invite landing page. */
    requestInviteOtp: (token: string) =>
        api.post<{ message: string; email: string }>(`${BASE}/invite/${token}/request-otp`),

    verifyInviteOtpAndAccept: (token: string, otp: string) =>
        api.post<{ message: string; accessToken: string; user: { id: string; email: string } }>(
            `${BASE}/invite/${token}/verify-and-accept`,
            { otp },
        ),
};

// Types for the preview-invite response
export type InvitePreviewStatus =
    | 'new_user'
    | 'existing_in_org'
    | 'existing_other_org'
    | 'already_member'
    | 'already_pending';

export interface InvitePreviewResult {
    email: string;
    status: InvitePreviewStatus;
    detail?: string;
}
